import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import Clickable from 'components/Common/Clickable'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import withMountGuard from 'hocs/withMountGuard'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { rem } from 'polished'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const Container = styled(Clickable)`
  padding: ${rem(12)} ${rem(16)};
  color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
  border-radius: ${props => props.theme.borderRadius.M};
  /** To see the effect of the border-radius */
  overflow: hidden;
`

interface Props {
  onLinkClick: () => void
}

function AccountMenuLoyaltyNoAccountBronzeTile({ onLinkClick }: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const handleActivateMembership = useCallback(() => {
    dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
    onLinkClick()
  }, [dispatch, onLinkClick])

  const bronzeLabel = intl.formatMessage({
    id: 'aB75EyjP1EC5Wv3FURyo',
    defaultMessage: 'Bronze',
    description: 'Label for the Bronze membership tier in the loyalty program tier progression display',
  })

  const exclusiveInviteLabel = intl.formatMessage({
    id: 'DgndYcWV8ZuJwvmsNktn',
    defaultMessage: 'EXCLUSIVE INVITE',
    description: 'Exclusive invite label for loyalty tier in the loyalty program section',
  })

  const unlockedMessage = useMemo(() => {
    return intl.formatMessage(
      {
        id: 'h4m1lkHKamjBNBkTvBnQ',
        defaultMessage: 'You’ve unlocked <bronzeStatus>Bronze status</bronzeStatus>',
        description: 'Message indicating user has unlocked Bronze status',
      },
      {
        bronzeStatus: (chunks: React.ReactNode) => (
          <Colour key="bronze-status" value="tier-bronze-light-contrast">
            {chunks}
          </Colour>
        ),
      },
    )
  }, [intl])

  const activateDescription = intl.formatMessage({
    id: 'xlbGdDuj47qcGmDefmVk',
    defaultMessage: 'Activate now to start earning points and Status credits on your next booking',
    description: 'Description text encouraging user to activate membership',
  })

  const activateButtonLabel = intl.formatMessage({
    id: '6s7T1mheugtdtW37DNm0',
    defaultMessage: 'Activate free membership',
    description: 'Button label to activate free membership for the membership',
  })

  return <Container to={`/${LOYALTY_LANDING_PAGE}`} onClick={onLinkClick}>
    <VerticalSpacer gap={20}>
      <VerticalSpacer gap={12}>
        <Group direction="horizontal" horizontalAlign="space-between">
          <LoyaltyProgramNameLogo width={72} />
          <BodyText variant="medium" format="uppercase">{bronzeLabel}</BodyText>
        </Group>
        <LoyaltyTierLabel
          tier="bronze"
          kind="primary"
          format="uppercase"
        >
          {exclusiveInviteLabel}
        </LoyaltyTierLabel>
        <VerticalSpacer gap={8}>
          <Heading variant="heading4">
            {unlockedMessage}
          </Heading>
          <BodyText variant="small">
            {activateDescription}
          </BodyText>
        </VerticalSpacer>
      </VerticalSpacer>
      <TextButton kind="primary" size="medium" fit="full-width" onClick={handleActivateMembership}>
        {activateButtonLabel}
      </TextButton>
    </VerticalSpacer>
  </Container>
}

export default withMountGuard(() => {
  return useAppSelector(getIsLoyaltyEnabled)
})(AccountMenuLoyaltyNoAccountBronzeTile)
